import useSession from '@/hooks/useSession';
import styles from './list.module.scss';
import { NavLink } from 'react-router-dom';
import { FC, useState } from 'react';
import LoadingSpinner from '../LoadingSpinner';

type SourceFilter = 'all' | 'upload' | 'sharepoint';

interface ListProps {
  items: Array<{
    path: string;
    title: string;
    badge?: string;
    subtitle: string;
    isLoading?: boolean;
    onClick?: () => void;
    source?: 'upload' | 'sharepoint';
  }>;
  enableSearch?: boolean;
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  sourceFilter?: SourceFilter;
  onSourceFilterChange?: (filter: SourceFilter) => void;
  totalCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const List: FC<ListProps> = ({
  items = [],
  enableSearch = false,
  searchTerm,
  onSearchChange,
  sourceFilter,
  onSourceFilterChange,
  totalCount,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}) => {
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [internalSourceFilter, setInternalSourceFilter] =
    useState<SourceFilter>('all');
  const effectiveSearchTerm =
    searchTerm !== undefined ? searchTerm : internalSearchTerm;
  const effectiveSourceFilter =
    sourceFilter !== undefined ? sourceFilter : internalSourceFilter;
  const handleSearchChange = onSearchChange ?? setInternalSearchTerm;
  const handleSourceFilterChange =
    onSourceFilterChange ?? setInternalSourceFilter;

  const hasSharepointItems = items.some((item) => item.source === 'sharepoint');

  const sourceFilteredItems =
    hasSharepointItems && effectiveSourceFilter !== 'all'
      ? items.filter((item) => item.source === effectiveSourceFilter)
      : items;

  const filteredItems =
    enableSearch && effectiveSearchTerm
      ? sourceFilteredItems.filter((item) =>
          item.title.toLowerCase().includes(effectiveSearchTerm.toLowerCase())
        )
      : sourceFilteredItems;
  const hasActiveFilter =
    !!effectiveSearchTerm || effectiveSourceFilter !== 'all';
  const isFilterLoading = hasActiveFilter && (isLoadingMore || hasMore);

  const getSummaryText = () => {
    if (isFilterLoading) return null;
    if (items.length === 0) return 'No items available';
    if (filteredItems.length === 0 && effectiveSearchTerm)
      return 'No results found';
    if (hasActiveFilter)
      return `Showing ${filteredItems.length} of ${totalCount ?? items.length}`;
    const count = totalCount ?? items.length;
    return `${count} ${count === 1 ? 'item' : 'items'}`;
  };
  const { pushUserEvent } = useSession();
  return (
    <>
      {enableSearch && (
        <div className={styles['search-wrapper']}>
          <input
            type="search"
            placeholder="Search..."
            className={styles['search']}
            value={effectiveSearchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            aria-label="Search items"
          />
          {hasSharepointItems && (
            <div className={styles['source-filter']}>
              {(['all', 'sharepoint', 'upload'] as SourceFilter[]).map((f) => (
                <button
                  key={f}
                  className={`${styles['source-filter__pill']}${effectiveSourceFilter === f ? ` ${styles['source-filter__pill--active']}` : ''}`}
                  onClick={() => handleSourceFilterChange(f)}
                >
                  {f === 'all'
                    ? 'All'
                    : f === 'sharepoint'
                      ? 'Sharepoint'
                      : 'Upload'}
                </button>
              ))}
            </div>
          )}
          <span>{getSummaryText()}</span>
        </div>
      )}
      <ul className={styles['list']}>
        {isFilterLoading && (
          <li className={styles['list__filter-loading']}>
            <LoadingSpinner />
          </li>
        )}
        {filteredItems.length > 0 && (
          <>
            {filteredItems.map(
              ({ title, badge, subtitle, path, isLoading, onClick }, i) => {
                return (
                  <li key={i}>
                    <NavLink
                      data-testid="list-item"
                      to={path}
                      onClick={() => {
                        if (isLoading) {
                          return;
                        }
                        pushUserEvent('click_list_item');
                        if (onClick) {
                          onClick();
                        }
                      }}
                      data-loading={isLoading}
                      title={title}
                    >
                      {isLoading && (
                        <LoadingSpinner className={styles['item-icon']} />
                      )}

                      {title}
                      <p>
                        <span className={styles['item-subtitle']}>
                          {subtitle}
                        </span>

                        {typeof badge !== 'undefined' && (
                          <i className={styles['item-badge']}>Sharepoint</i>
                        )}
                        {isLoading && <strong>[PROCESSING]</strong>}
                      </p>
                    </NavLink>
                  </li>
                );
              }
            )}
          </>
        )}

        {!isFilterLoading && hasMore && (
          <li className={styles['load-more']}>
            <button
              onClick={isLoadingMore ? undefined : onLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading...' : 'Load more'}
            </button>
          </li>
        )}
      </ul>
    </>
  );
};

export default List;
